import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import moment from 'moment'
import { observer } from 'mobx-react-lite'
import { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react'
import { SectionCard } from 'app/components/SectionCard'
import { Button } from 'app/components/ui/button'
import { Tooltip } from 'app/components/Tooltip'
import {
  AvailabilityDay,
  AvailabilityWindow,
  ISO_FORMAT,
  MAX_MONTH_OFFSET,
  PAST_CELL_CLASS,
  extractTimeLabel,
  headline,
  availabilityWindow,
  offsetForArrowKey,
  tierForCount,
} from './locationAvailability.utils'
import { TypeSetupDetails } from './TypeSetupDetails'
import { useAppointmentTypeSetups, type AppointmentTypeSetup } from './useAppointmentTypeSetups'
import { AppointmentTypeAvailability, LocationAvailability, useLocationAvailability } from './useLocationAvailability'

/**
 * Availability overview for the locations selected in the dashboard header
 * (TRK-5318): one card per agent-bookable appointment type, leading with when
 * the next opening is and carrying a heat strip for the month underneath.
 *
 * The strip is a continuous left-to-right run rather than a calendar block, so
 * four cards fit on one row and the shape of the month reads at a glance; the
 * per-day detail lives in the hover, which names the date, the open-slot count
 * and the actual time blocks behind it.
 */

/**
 * Whether a keystroke belongs to whatever the user is typing in. Arrow keys
 * must yield here or the caret stops moving in any focused field. jsdom never
 * sets `isContentEditable`, so fall back to the attribute the way
 * `global-search/shortcut.ts` does.
 */
function isTextEntryTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false
  const tag = target.tagName
  if (tag === `INPUT` || tag === `TEXTAREA` || tag === `SELECT`) return true
  if (target.isContentEditable === true) return true
  const root = target.closest(`[contenteditable]`)
  return root != null && root.getAttribute(`contenteditable`) !== `false`
}

/** One day cell, plus the tooltip it raises while hovered. */
function HeatCell({
  day,
  isPast,
  isDimmed,
  isToday,
  onHover,
}: {
  day: AvailabilityDay
  isPast: boolean
  isDimmed: boolean
  isToday: boolean
  onHover: () => void
}): ReactElement {
  const cell = (
    <div
      className={classNames(
        `h-7 w-full rounded-sm border transition-opacity duration-100`,
        isPast ? PAST_CELL_CLASS : tierForCount(day.count).className,
        isDimmed && `opacity-35`,
        isToday && `ring-1 ring-strong ring-offset-1`,
      )}
    />
  )

  // A past day was never fetched, so there is nothing to say about it.
  if (isPast) return <div className="min-w-0 flex-1">{cell}</div>

  const times = day.timeSlots.map((slot) => extractTimeLabel(slot.time)).filter(Boolean)

  return (
    <Tooltip
      placement="top"
      className="min-w-0 flex-1"
      onMouseOver={onHover}
      message={
        <div className="flex max-w-xs flex-col gap-0.5 p-1">
          <p className="text-body-xs inverse-text-default">{moment(day.date, ISO_FORMAT).format(`ddd, MMM D`)}</p>
          <p className="text-ui-sm tabular-nums inverse-text-strong">
            {day.count === 0 ? `No availability` : `${day.count} open slot${day.count === 1 ? `` : `s`}`}
          </p>
          {times.length > 0 && <p className="text-body-xs inverse-text-default">{times.join(`, `)}</p>}
        </div>
      }
    >
      {cell}
    </Tooltip>
  )
}

/** The month as one continuous run of day cells, hovered one at a time. */
function HeatStrip({ days, todayISO }: { days: AvailabilityDay[]; todayISO: string }): ReactElement {
  const [hoveredDate, setHoveredDate] = useState<string | null>(null)

  const first = days[0]?.date
  const last = days[days.length - 1]?.date
  return (
    <div className="flex flex-col gap-1">
      <div className="flex gap-1" onMouseLeave={() => setHoveredDate(null)}>
        {days.map((day) => (
          <HeatCell
            key={day.date}
            day={day}
            isPast={day.date < todayISO}
            isDimmed={hoveredDate !== null && hoveredDate !== day.date}
            isToday={day.date === todayISO}
            onHover={() => setHoveredDate(day.date)}
          />
        ))}
      </div>
      {first && last && (
        <div className="flex justify-between text-body-xs text-minimal">
          <span>{first === todayISO ? `Today` : moment(first, ISO_FORMAT).format(`MMM D`)}</span>
          <span>{moment(last, ISO_FORMAT).format(`MMM D`)}</span>
        </div>
      )}
    </div>
  )
}

function TypeCard({
  type,
  window,
  setup,
  setupLoading,
  todayISO,
}: {
  type: AppointmentTypeAvailability
  window: AvailabilityWindow
  setup: AppointmentTypeSetup | null | undefined
  setupLoading: boolean
  todayISO: string
}): ReactElement {
  if (type.status === `error`) {
    return (
      <div className="rounded-lg border border-default p-3">
        <p className="text-body-xs text-subtle">{type.label}</p>
        <p className="mt-2 text-body-sm text-subtle">Availability unavailable for this type.</p>
      </div>
    )
  }

  const { value, caption } = headline(type.days, window)

  return (
    <div className="rounded-lg border border-default p-4">
      <p className="mb-1 text-body-xs text-subtle">{type.label}</p>
      <p className="mb-4 text-heading-lg tabular-nums text-strong">
        {value}
        <span className="ml-1.5 text-body-xs text-subtle">{caption}</span>
      </p>
      <HeatStrip days={type.days} todayISO={todayISO} />
      <TypeSetupDetails setup={setup} loading={setupLoading} />
    </div>
  )
}

function TypeSkeleton(): ReactElement {
  return <div className="h-34 animate-pulse rounded-lg bg-surface-muted" aria-busy="true" />
}

/**
 * Walks the shown month back and forth. Backwards stops at the current month —
 * availability in the past is not a thing — and forwards stops at
 * `MAX_MONTH_OFFSET`, past which a practice's schedule is rarely open yet.
 */
function MonthStepper({
  label,
  offset,
  onChange,
}: {
  label: string
  offset: number
  onChange: (offset: number) => void
}): ReactElement {
  return (
    <div className="flex items-center gap-2">
      <Button
        type="button"
        variant="tertiary"
        size="icon-sm"
        aria-label="Earlier days"
        title="Earlier days (←)"
        disabled={offset <= 0}
        onClick={() => onChange(offset - 1)}
      >
        <ChevronLeftIcon className="h-4 w-4" />
      </Button>
      <span className="min-w-34 text-center text-ui-sm text-strong">{label}</span>
      <Button
        type="button"
        variant="tertiary"
        size="icon-sm"
        aria-label="Later days"
        title="Later days (→)"
        disabled={offset >= MAX_MONTH_OFFSET}
        onClick={() => onChange(offset + 1)}
      >
        <ChevronRightIcon className="h-4 w-4" />
      </Button>
    </div>
  )
}

const LocationCards = observer(function LocationCards({
  location,
  window,
  headerRight,
  showName,
}: {
  location: LocationAvailability
  window: AvailabilityWindow
  /** With more than one practice on the page, each card names its own. */
  showName: boolean
  /** Page-level control parked on this section's title line; only the first section gets one. */
  headerRight?: ReactNode
}): ReactElement {
  const { setups, loading, today } = useAppointmentTypeSetups(location.locationId)
  return (
    <SectionCard
      attached
      title={showName ? `Availability Preview · ${location.locationName}` : `Availability Preview`}
      info="The openings your agent can offer callers right now. Contact support if you'd like to change them."
      headerButtons={headerRight}
      contentClassName="bg-surface"
    >
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        {location.appointmentTypes.map((type, i) =>
          type ? (
            <TypeCard
              key={type.key}
              type={type}
              window={window}
              setup={loading ? undefined : setups[type.key]}
              setupLoading={loading}
              todayISO={today}
            />
          ) : (
            <TypeSkeleton key={i} />
          ),
        )}
      </div>
    </SectionCard>
  )
})

const MESSAGE_SHELL = `flex h-40 items-center justify-center rounded-lg border border-default text-body-sm text-subtle`

/**
 * The Availability tab: one location at a time, because every location costs four live PMS
 * round trips per month (CareStack bills per call). The page header picks the location.
 */
export function AvailabilityWidget({
  locationId,
  isLoading,
}: {
  locationId?: string
  isLoading: boolean
}): ReactElement {
  if (!locationId) {
    return (
      <div className={MESSAGE_SHELL}>
        {isLoading ? `Loading locations…` : `No AI Agent location available for this workspace.`}
      </div>
    )
  }
  return <LocationAvailabilityPreview key={locationId} locationId={locationId} />
}

function LocationAvailabilityPreview({ locationId }: { locationId: string }): ReactElement {
  const [offset, setOffset] = useState(0)
  const window = useMemo(() => availabilityWindow(offset), [offset])
  const { locations, error } = useLocationAvailability(window, locationId)

  // Left/right step the month. Bound to the document rather than a focusable
  // container so it works without the user having to click into the tab first;
  // the listener lives and dies with this widget, which only mounts on the
  // Availability tab, so it cannot reach the other tabs.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent): void => {
      const next = offsetForArrowKey(event.key, offset, {
        hasModifier: event.metaKey || event.ctrlKey || event.altKey,
        isTyping: isTextEntryTarget(event.target),
      })
      if (next === null) return
      event.preventDefault()
      setOffset(next)
    }
    document.addEventListener(`keydown`, onKeyDown)
    return () => document.removeEventListener(`keydown`, onKeyDown)
  }, [offset])

  if (error) return <div className={MESSAGE_SHELL}>Could not load availability for this location.</div>
  const location = locations[0]
  if (!location) return <div className={MESSAGE_SHELL}>Loading availability…</div>

  return (
    <div className="mb-6">
      <LocationCards
        location={location}
        window={window}
        showName={false}
        headerRight={<MonthStepper label={window.label} offset={offset} onChange={setOffset} />}
      />
    </div>
  )
}
